import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { useSportsSearch } from '@/shell/SportsSearchContext';
import { HorizontalScrollRow, Icon } from '@/ui/common';
import {
  homeFilterMenuItems,
  type HomeFilterId,
  type HomeFilterMenuContext,
} from './home-filter-menu-config';
import { HomeFilterItem } from './HomeFilterItem';

export interface HomeFilterMenuRailProps {
  /** Controlled selection; `null` means nothing selected. */
  value?: HomeFilterId | null;
  defaultValue?: HomeFilterId | null;
  onValueChange?: (id: HomeFilterId) => void;
  /**
   * `prematch` (default): Live | Top Events | Today | Last Minute.
   * `live`: Prematch | Top Events | Today | Last Minute.
   * Favourites is inserted after the mode chip when `showFavorites` is true.
   */
  context?: HomeFilterMenuContext;
  /** When true, inserts the Favourites chip (only if the player has prematch favorites). */
  showFavorites?: boolean;
  className?: string;
}

/**
 * Figma HomeFilterMenuRail — horizontal filter chips + fixed SearchButton (mobile).
 * Supports controlled and uncontrolled selection.
 */
export function HomeFilterMenuRail({
  value,
  defaultValue = null,
  onValueChange,
  context = 'prematch',
  showFavorites = false,
  className,
}: HomeFilterMenuRailProps) {
  const { t } = useTranslation();
  const sportsSearch = useSportsSearch();
  const [uncontrolled, setUncontrolled] = useState<HomeFilterId | null>(defaultValue);
  const selected = value !== undefined ? value : uncontrolled;
  const items = homeFilterMenuItems(context, { showFavorites });

  const select = (id: HomeFilterId) => {
    if (value === undefined) {
      setUncontrolled(id);
    }
    onValueChange?.(id);
  };

  return (
    <div className={cn('flex h-8 items-center gap-2', className)}>
      <HorizontalScrollRow
        aria-label={t('Event filters', { defaultValue: 'Event filters' })}
        className="h-8 min-w-0 flex-1 items-center gap-2 pl-0"
      >
        {items.map((item) => (
          <HomeFilterItem
            key={item.id}
            label={t(item.translateKey, { defaultValue: item.label })}
            icon={item.icon}
            selected={item.id === selected}
            onClick={() => select(item.id)}
          />
        ))}
      </HorizontalScrollRow>

      {sportsSearch ? (
        <button
          type="button"
          onClick={sportsSearch.openSearch}
          aria-label={t('Search events', { defaultValue: 'Search events' })}
          className={cn(
            // Figma SearchButton: fixed beside scrolling filters (mobile rail).
            'flex size-8 shrink-0 items-center justify-center rounded-md border border-border-default bg-bg-surface',
            'text-icon-default hover:border-border-focus focus-visible:border-border-focus focus-visible:outline-none',
            'lg:hidden',
          )}
        >
          <Icon name="search" size={14} />
        </button>
      ) : null}
    </div>
  );
}
